/**
 * Храналище выполненное по аналогии с https://pinia.vuejs.org/
 *
 * Основное отличие от Pinia - реализация Храналища на уровне компонента, а не приложения.
 * Каждый экземдяр компонента будет иметь свой Store.
 *
 * Основное отличие от классического State - является общим для компонента и всех его дочерних компонентов. Основано на принципе provide/inject.
 * https://vuejs.org/guide/components/provide-inject.html
 *
 * @packageDocumentation
 */

import type { InjectionKey, UnwrapNestedRefs } from 'vue';
import { inject, provide, reactive, watch } from 'vue';
import { usePlugins } from './plugin';

/**
 * Определить Store
 *
 * Следует определять на уровне корневого компонента, использующего Store
 * @param injectionKey - InjectionKey для provide / inject в области компонента
 * @param setup - Нстройки Store, см. https://pinia.vuejs.org/core-concepts/#Setup-Stores
 * @param $id - строковой id Store, используется в плагинах
 * @param options - настройки Store, используются в плагинах
 */
export const defineStore = <T extends object>(
	injectionKey: InjectionKey<UnwrapNestedRefs<T>>,
	setup: () => T | object,
	$id?: string,
	options?: object,
): UnwrapNestedRefs<T> & Store => {
	const store = reactive(setup()) as UnwrapNestedRefs<T> & Store;

	store.$id = $id;
	store.$subscribe = (cb: Function) => {
		watch(store, () => cb(), { deep: true });
	};

	usePlugins(store, options);

	provide(injectionKey, store);

	return store;
};

/**
 * Получить State компонента
 * @param injectionKey - InjectionKey для provide / inject в области компонента
 * @return - State компонента
 */
export const useStore = <T>(injectionKey: InjectionKey<T>): T & Store => {
	const store = inject(injectionKey);

	if (!store) {
		throw Error('Попытка использовать Store до его инициализации, убедитесь, что defineStore() вызван в setup() необходимого компонента');
	}

	return store;
};

/**
 * Легковесный аналог объекта Store Pinia для совместимости с плагинами Pinia
 */
export interface Store {
	/**
	 * Строковой идентификатор Store
	 *
	 * Не уникален в рамках приложения
	 */
	$id?: string;
	$subscribe?: Function;
}

export default {
	defineStore,
	useStore,
};
